<template>
  <div
    class="flex flex-col rounded-2xl"
    :class="[
      isCompactSidebar ? 'gap-3' : 'gap-3 sm:gap-4',
      resolvedBackground === 'card' && 'lot-side-surface !bg-white px-4 py-5',
    ]"
  >
    <div
      class="flex gap-4"
      :class="isCompactSidebar ? 'flex-col' : 'flex-col sm:flex-row sm:items-start'"
    >
      <div class="flex flex-1 min-w-0 gap-4 items-start">
        <NuxtLink
          :to="$localePath(`/user/${user.id}`)"
          class="rounded-full h-10 w-10 shrink-0 overflow-hidden"
          :aria-label="`Перейти к профилю ${user.publicName}`"
        >
          <nuxt-img
            v-if="user?.avatar?.medium"
            :src="user.avatar.medium"
            alt="avatar"
            class="object-cover w-full h-full"
            format="webp"
            loading="lazy"
            width="40"
            height="40"
          />
        </NuxtLink>

        <div class="flex flex-col gap-2 min-w-0 flex-1">
          <div class="flex flex-col items-start">
            <NuxtLink
              :to="$localePath(`/user/${user.id}`)"
              class="font-bold text-lg leading-7 text-gray-900"
              :aria-label="`Перейти к профилю ${user.publicName}`"
            >
              {{ user.publicName }}
            </NuxtLink>

            <div
              v-if="user.avgRating || rentReviews?.length"
              class="flex items-center gap-1"
            >
              <RmStars
                v-if="user.avgRating"
                :id="user.id"
                :rating="user.avgRating"
              />
              <span
                v-if="user.avgRating"
                class="text-sm font-semibold text-gray-900"
              >
                {{ user.avgRating }}
              </span>
              <button
                v-if="rentReviews?.length"
                class="text-blue-500 text-xs underline"
                @click="goToReviews"
              >
                {{ rentReviews.length }}
                {{ $t('lots.id.reviews', rentReviews.length) }}
              </button>
            </div>
          </div>

          <div
            v-if="address"
            class="flex flex-col gap-1"
          >
            <div class="flex gap-1 items-start">
              <MapMarketLineIcon class="size-4 text-gray-600 shrink-0" />



              <span class="text-sm font-medium text-gray-900 leading-5">
                {{ address }}
              </span>
            </div>
            <div>

              <button
                class="text-blue-500 text-sm font-medium underline"
                @click="showMap"
              >
                Показать на карте
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="!isOwnerLot && !isCompactSidebar"
        class="hidden lg:flex flex-col gap-2 shrink-0 w-full lg:w-48"
      >
        <PolkaBtn
          :color="isPrimaryButtons ? 'primary' : 'secondary'"
          :size="isPrimaryButtons ? 'large' : 'medium'"
          :class="messageButtonClass"
          @click="handleAskQuestion"
        >
          <span :class="isPrimaryButtons ? 'text-base font-semibold' : 'text-sm font-semibold'">
            Написать
          </span>
        </PolkaBtn>

        <LotsShowPhoneButton
          v-if="showPhoneButton"
          :phone="ownerPhone"
          :color="isPrimaryButtons ? 'success' : 'secondary'"
          :button-class="phoneButtonClass"
          @click="handleCall"
        />
      </div>
    </div>

    <LazyVisibleLotsShowExpandableText
      v-if="showDescriptionBlock"
      :text="user.description"
      :max-lines="4"
      :font-size="0.875"
      hydrate-on-visible
    >
      <div class="font-medium text-gray-700 text-sm whitespace-pre-line leading-6">
        <template
          v-for="(part, index) in processedDescription"
          :key="part.isLink ? `link-${index}` : `text-${index}`"
        >
          <a
            v-if="part.isLink"
            href="https://spb.crazymamas.ru/"
            target="_blank"
            rel="noopener noreferrer"
            class="text-blue-500 hover:text-blue-700 underline"
          >{{ part.text }}</a><span v-else>{{ part.text }}</span>
        </template>
      </div>
    </LazyVisibleLotsShowExpandableText>

    <div
      v-if="!isOwnerLot"
      ref="contactActionsRef"
      class="flex flex-col gap-2 w-full"
      :class="!isCompactSidebar && 'lg:hidden'"
    >
      <PolkaBtn
        :color="isPrimaryButtons ? 'primary' : 'secondary'"
        :size="isPrimaryButtons ? 'large' : 'medium'"
        :class="messageButtonClass"
        @click="handleAskQuestion"
      >
        <span :class="isPrimaryButtons ? 'text-base font-semibold' : 'text-sm font-semibold'">
          Написать
        </span>
      </PolkaBtn>

      <LotsShowPhoneButton
        v-if="showPhoneButton"
        :phone="ownerPhone"
        :color="isPrimaryButtons ? 'success' : 'secondary'"
        :button-class="phoneButtonClass"
        @click="handleCall"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { nextTick, onMounted, ref } from 'vue'
import { buildAnalyticsStore } from '~/analytics/composables-types'
import MapMarketLineIcon from '~/assets/svg/ui/map-marker-line.svg?component'
import LotsShowPhoneButton from '~/components/lots/show/phone_button.vue'
import PolkaBtn from '~/components/shared/polka_btn.vue'
import RmStars from '~/components/shared/rm_stars_new.vue'
import {
  getClickOwnerCallAmplitudePayload,
  getOwnerCallType,
  getOwnerPhoneType,
} from '~/composables/useClickOwnerCallAnalytics'
import { getClickOwnerChatAmplitudePayload } from '~/composables/useClickOwnerChatAnalytics'
import { useLot } from '~/composables/useLot'
import { useLotSafeDeal } from '~/composables/useLotSafeDeal'
import { useScrollTo } from '~/composables/useScrollTo'
import { useUserDescription } from '~/composables/useUserDescription'
import { useView } from '~/composables/useView.js'

const props = defineProps({
  user: {
    type: Object,
    default: () => ({}),
  },
  lotId: {
    type: [String, Number],
    required: true,
  },
  minRentDays: {
    type: Number,
    default: 1,
  },
  address: {
    type: String,
    default: '',
  },
  location: {
    type: Object,
    default: () => ({}),
  },
  layout: {
    type: String,
    default: '',
  },
  buttons: {
    type: String,
    default: 'secondary',
  },
  phone: {
    type: Boolean,
    default: undefined,
  },
  showDescription: {
    type: Boolean,
    default: true,
  },
  background: {
    type: String,
    default: '',
  },
  inLeftColumn: {
    type: Boolean,
    default: false,
  },
})

const { scrollTo } = useScrollTo()
const { $localePath, $tracking, $modal, $breakpoints } = useNuxtApp()
const { lot } = useLot(props.lotId)
const { currentUserId } = useAuth()
const { isSafeDealActive } = useLotSafeDeal(lot)
const showBottomPanelInLot = useGlobal().showBottomPanelInLot
const contactActionsRef = ref(null)

const isBelowLg = computed(() => $breakpoints.width < 1024)

const rentReviews = computed(() => lot.value.user?.rentReviews || [])

const userRef = computed(() => props.user)
const { processedDescription } = useUserDescription(userRef)


const isOwnerLot = computed(() => {
  return Number(currentUserId.value) === Number(props.user.id)
})

const updateContactBottomBarVisibility = (e) => {
  if (!import.meta.client || isSafeDealActive.value || !isBelowLg.value || isOwnerLot.value) {
    return
  }

  if (
    e.percentCenter === 0 &&
    e.percentInView === 0 &&
    e.percentTop === 0 &&
    e.scrollPercent === 0
  ) {
    showBottomPanelInLot.value = false
    return
  }

  if (e.percentInView < 0.8) {
    showBottomPanelInLot.value = true
  } else {
    showBottomPanelInLot.value = false
  }
}

useView(contactActionsRef, {
  handler: updateContactBottomBarVisibility,
})

onMounted(() => {
  nextTick(() => {
    if (!contactActionsRef.value || isSafeDealActive.value || !isBelowLg.value || isOwnerLot.value) {
      return
    }

    const el = contactActionsRef.value
    if (!el?.getBoundingClientRect) {
      return
    }

    const rect = el.getBoundingClientRect()
    const viewportHeight = globalThis.innerHeight
    const isVisible = rect.top < viewportHeight && rect.bottom > 0

    showBottomPanelInLot.value = !isVisible
  })
})

const ownerPhone = computed(() => {
  return props.user?.phoneProxy || props.user?.phonePublic || null
})

const resolvedLayout = computed(() => {
  if (props.layout === 'sm' || props.layout === 'sidebar') {
    return props.layout
  }
  if (props.inLeftColumn) {
    return 'sidebar'
  }
  if (!isBelowLg.value) {
    return 'sm'
  }
  return 'sidebar'
})

const resolvedBackground = computed(() => {
  if (props.background === 'card' || props.background === 'none') {
    return props.background
  }
  return props.inLeftColumn ? 'card' : 'none'
})

const isCompactSidebar = computed(() => {
  return resolvedLayout.value === 'sidebar' && resolvedBackground.value === 'card'
})

const isPrimaryButtons = computed(() => props.buttons === 'primary')

const showPhoneButton = computed(() => {
  if (!ownerPhone.value) {
    return false
  }
  if (props.phone !== undefined) {
    return props.phone
  }
  return true
})

const showDescriptionBlock = computed(() => {
  return props.showDescription && Boolean(props.user?.description)
})

const messageButtonClass = computed(() => {
  if (isPrimaryButtons.value) {
    return 'w-full rounded-2xl'
  }
  return 'w-full rounded-lg !bg-gray-300 !text-gray-900 hover:!bg-gray-200 active:!bg-gray-300 !border-0'
})

const phoneButtonClass = computed(() => {
  if (isPrimaryButtons.value) {
    return 'w-full rounded-2xl'
  }
  return 'w-full rounded-lg'
})

const handleCall = ({ canDial }) => {
  if (!lot.value || !ownerPhone.value) return

  try {
    $tracking.logEventYA('click_owner_call')
  } catch {
    // ym может быть недоступен в dev
  }

  try {
    $tracking.logEventAmplitude(
      'click owner call',
      getClickOwnerCallAmplitudePayload({
        store: buildAnalyticsStore(),
        lot: lot.value,
        user: props.user,
        callType: getOwnerCallType(canDial),
        phoneType: getOwnerPhoneType({
          userPhone: props.user?.phone,
          ownerPhone: ownerPhone.value,
          phonePublic: props.user?.phonePublic,
          phoneProxy: props.user?.phoneProxy,
        }),
      }),
    )
  } catch {
    // не блокируем звонок из-за ошибки аналитики
  }
}

const showMap = () => {
  $modal.show('shared/modal/map', {
    location: props.location,
    address: props.address,
    analytics: true,
    lot: lot.value,
    owner: lot.value.user,
    ownerCity: lot.value.city.name,
  })
}

const goToReviews = () => {
  scrollTo('#rentReviews', 200, {
    offset: -40,
  })
}

const handleAskQuestion = () => {
  try {
    $tracking.logEventYA('click_chat_product')
  } catch {
    // ym может быть недоступен в dev
  }

  try {
    $tracking.logEventAmplitude(
      'click owner chat',
      getClickOwnerChatAmplitudePayload({
        store: buildAnalyticsStore(),
        lot: lot.value,
        user: props.user,
      }),
    )
  } catch {
    // не блокируем чат из-за ошибки аналитики
  }

  $modal.show('flow/go_chat_from_lot', { lotId: props.lotId })
}
</script>
